import React, { useRef, useEffect, useState } from 'react';
import { PiBlueprintLight } from "react-icons/pi";
import { MdPowerSettingsNew, MdTouchApp, MdTimer, MdCheckCircle } from "react-icons/md";
import './stickerViewer.css';

function StickerViewer({ lastUpdatedSticker, selectedStickerIdx, setSelectedStickerIdx, stickers }) {
    const [flashingSticker, setFlashingSticker] = useState(null);
    const [layout, setLayout] = useState('a4'); // 'a4' or 'roll'
    const flashTimeoutRef = useRef(null);

    useEffect(() => {
        if (lastUpdatedSticker != null) {
            setFlashingSticker(lastUpdatedSticker);
            if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
            flashTimeoutRef.current = setTimeout(() => {
                setFlashingSticker(null);
                flashTimeoutRef.current = null;
            }, 3000);
            return () => {
                if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
            };
        }
    }, [lastUpdatedSticker]);

    // Check if at least one sticker has Product data
    const canPrint = stickers.some(sticker => sticker.Product);

    // Print handler - opens new tab with print-stickers page
    const handlePrintClick = () => {
        // Get stickers with product data
        const stickersWithData = stickers.filter(sticker => sticker.Product);
        
        if (stickersWithData.length === 0) {
            console.warn('No stickers with product data to print');
            return;
        }
        
        // Prepare sticker data for URL
        // Note: sticker.Product is the description STRING, not an object
        const stickerData = stickersWithData.map((sticker, index) => ({
            ProductId: sticker.EAN || index, // Use EAN as ID, fallback to index
            ProductCode: sticker.EAN, // EAN is the barcode code
            ProductDescription: sticker.Product // Product is the description string
        }));
        
        console.log('Prepared sticker data:', stickerData); // Debug log
        
        // Encode data as URL parameter
        const stickersParam = encodeURIComponent(JSON.stringify(stickerData));
        const url = `/#/print-stickers?stickers=${stickersParam}&layout=${layout}`;
        
        console.log('Opening print URL:', url); // Debug log
        
        // Open in new tab
        window.open(url, '_blank');
    };

    return (
        <div id='divenv' className="sticker-viewer-outer">
            <div className="sticker-viewer-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <PiBlueprintLight size={42} />
                    <h2>PREVIEW ETIQUETAS</h2>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            type="button"
                            onClick={() => setLayout('a4')}
                            className="print-btn"
                            style={{
                                padding: '8px 16px',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                borderRadius: 6,
                                border: '1.5px solid #385e96',
                                background: layout === 'a4' ? '#385e96' : '#fff',
                                color: layout === 'a4' ? '#fff' : '#385e96',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            A4 24 Etiquetas
                        </button>
                        <button
                            type="button"
                            disabled
                            className="print-btn"
                            style={{
                                padding: '8px 16px',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                borderRadius: 6,
                                border: '1.5px solid #ccc',
                                background: '#e5e7eb',
                                color: '#999',
                                cursor: 'not-allowed',
                                transition: 'all 0.2s',
                                opacity: 0.6
                            }}
                        >
                            Rollo 76mm 70x30
                        </button>
                    </div>
                    <button
                        type="button"
                        className="print-btn"
                        onClick={handlePrintClick}
                        disabled={!canPrint}
                        style={{
                            padding: '8px 18px',
                            fontWeight: 600,
                            fontSize: '1rem',
                            borderRadius: 6,
                            border: '1.5px solid #385e96',
                            background: canPrint ? '#385e96' : '#e5e7eb',
                            color: canPrint ? '#fff' : '#888',
                            cursor: canPrint ? 'pointer' : 'not-allowed',
                            transition: 'background 0.2s'
                        }}
                    >
                        Imprimir
                    </button>
                </div>
            </div>
            
            <div style={{ display: 'flex', gap: 48, alignItems: 'flex-start' }}>
                <div className={`sticker-grid-responsive print-grid ${layout === 'roll' ? 'roll-layout' : ''}`} style={{ flex: 1, maxWidth: layout === 'roll' ? '320px' : 'none' }}>
                {stickers.slice(0, layout === 'roll' ? 3 : 27).map((sticker, idx) => (
                    <button
                        key={idx}
                        className={`sticker-btn-grid${selectedStickerIdx === idx ? ' sticker-btn-selected' : ''}`}
                        onClick={() => setSelectedStickerIdx(idx)}
                        tabIndex={-1}
                    >
                        <span className="sticker-number-grid">{idx + 1}</span>
                        <div className="sticker-content-grid">
                            {sticker.Product && (
                                <div className="sticker-product-grid">{sticker.Product}</div>
                            )}
                            <div className="barcode-container-grid">
                                {sticker.Barcode
                                    ? <span dangerouslySetInnerHTML={{ __html: sticker.Barcode }} />
                                    : (!sticker.Product ? <span className="disponible-text">Disponible</span> : 'Barcode')}
                            </div>
                        </div>
                    </button>
                ))}
                </div>
                
                {/* Calibration Guide for Roll Layout */}
                {layout === 'roll' && (
                    <div className="calibration-guide" style={{
                        flex: '0 0 280px',
                        background: '#fff',
                        border: '1.5px solid #d1d5db',
                        borderRadius: 12,
                        padding: '16px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 8, 
                            marginBottom: 16,
                            paddingBottom: 12,
                            borderBottom: '1.5px solid #e5e7eb'
                        }}>
                            <span style={{ fontSize: '1.5rem' }}>⚙️</span>
                            <strong style={{ fontSize: '1rem', color: '#374151' }}>Calibración Térmica</strong>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {/* Step 1 */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ 
                                    width: 36, 
                                    height: 36, 
                                    background: '#4b5563',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <MdPowerSettingsNew size={20} color="#fff" />
                                </div>
                                <span style={{ fontSize: '0.875rem', color: '#4b5563', fontWeight: 500 }}>
                                    Apagar impresora
                                </span>
                            </div>
                            
                            {/* Step 2 */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ 
                                    width: 36, 
                                    height: 36, 
                                    background: '#6b7280',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <MdTouchApp size={20} color="#fff" />
                                </div>
                                <span style={{ fontSize: '0.875rem', color: '#4b5563', fontWeight: 500 }}>
                                    Mantener botón <strong>FEED</strong>
                                </span>
                            </div>
                            
                            {/* Step 3 */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ 
                                    width: 36, 
                                    height: 36, 
                                    background: '#6b7280',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <MdPowerSettingsNew size={20} color="#fff" />
                                </div>
                                <span style={{ fontSize: '0.875rem', color: '#4b5563', fontWeight: 500 }}>
                                    Encender sin soltar
                                </span>
                            </div>
                            
                            {/* Step 4 */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ 
                                    width: 36, 
                                    height: 36, 
                                    background: '#9ca3af',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <MdTimer size={20} color="#fff" />
                                </div>
                                <span style={{ fontSize: '0.875rem', color: '#4b5563', fontWeight: 500 }}>
                                    Esperar 3-5 seg
                                </span>
                            </div>
                            
                            {/* Step 5 */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ 
                                    width: 36, 
                                    height: 36, 
                                    background: '#4b5563',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <MdCheckCircle size={20} color="#fff" />
                                </div>
                                <span style={{ fontSize: '0.875rem', color: '#4b5563', fontWeight: 500 }}>
                                    Soltar cuando pare
                                </span>
                            </div>
                        </div>
                        
                        <div style={{ 
                            marginTop: 16, 
                            paddingTop: 12,
                            borderTop: '1px solid #e5e7eb',
                            fontSize: '0.75rem', 
                            color: '#6b7280',
                            textAlign: 'center',
                            fontWeight: 600
                        }}>
                            🎯 Solo una vez o al cambiar rollo
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default StickerViewer;